import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { CONFIG } from 'src/config-global';
import { DashboardLayout } from 'src/layouts/dashboard';
import { AuthSplitLayout } from 'src/layouts/auth-split';

import { LoadingScreen } from 'src/components/loading-screen';
import { SplashScreen } from 'src/components/loading-screen';

import { AuthGuard, PermissionGuard, GuestGuard } from 'src/auth/guard';
import {
  AdminPermission,
  AddonPermission,
  BookingPermission,
  BannerPermission,
  CampaignPermission,
  CustomerGroupPermission,
  EventPermission,
  LocationPermission,
  RolePermission,
  SpacePermission,
  DashboardPermission,
  TransactionPermission,
  TournamentPermission,
  TournamentDivisionPermission,
  TournamentTeamPermission,
  TournamentGroupPermission,
  TournamentMatchPermission,
} from '../../../../../libs/role/src/lib/types/permission-type.type';
import { ModuleNameEnum } from '../../../../../libs/core/src/lib/enums';
import { DivisionLayout } from 'src/layouts/division/layout';

// Authentication
/** **************************************
 * Jwt
 *************************************** */
const Jwt = {
  SignInPage: lazy(() => import('src/pages/auth/sign-in')),
  ForgetPasswordPage: lazy(() => import('src/pages/auth/forget-password')),
  ResetPasswordPage: lazy(() => import('src/pages/auth/reset-password')),
};

// ----------------------------------------------------------------------
// Overview Stats
const OverviewBookingPage = lazy(
  () => import('src/pages/dashboard/overview/overview-booking-page'),
);
const OverviewFinancePage = lazy(
  () => import('src/pages/dashboard/overview/overview-finance-page'),
);
const OverviewLocationPage = lazy(
  () => import('src/pages/dashboard/overview/overview-location-page'),
);
const OverviewCustomerPage = lazy(
  () => import('src/pages/dashboard/overview/overview-customer-page'),
);
const OverviewUserStatisticsPage = lazy(
  () => import('src/pages/dashboard/overview/overview-user-statistics-page'),
);

// Profile
const ProfilePage = lazy(() => import('src/pages/dashboard/profile'));

// Locations
const LocationListPage = lazy(
  () => import('src/pages/dashboard/location/list'),
);
const LocationCreatePage = lazy(
  () => import('src/pages/dashboard/location/new'),
);
const LocationEditPage = lazy(
  () => import('src/pages/dashboard/location/edit'),
);
const LocationViewPage = lazy(
  () => import('src/pages/dashboard/location/view'),
);

// Banners
const BannersListPage = lazy(() => import('src/pages/dashboard/banners/list'));
const BannersCreatePage = lazy(() => import('src/pages/dashboard/banners/new'));
const BannersEditPage = lazy(() => import('src/pages/dashboard/banners/edit'));

// Addons
const AddonsListPage = lazy(() => import('src/pages/dashboard/addons/list'));
const AddonsCreatePage = lazy(() => import('src/pages/dashboard/addons/new'));
const AddonsEditPage = lazy(() => import('src/pages/dashboard/addons/edit'));

// Space
const SpaceListPage = lazy(() => import('src/pages/dashboard/space/list'));
const SpaceCreatePage = lazy(() => import('src/pages/dashboard/space/new'));
const SpaceEditPage = lazy(() => import('src/pages/dashboard/space/edit'));
const SpaceViewPage = lazy(() => import('src/pages/dashboard/space/view'));

// Bookings
const BookingsListPage = lazy(
  () => import('src/pages/dashboard/bookings/list'),
);
const BookingsCalendarPage = lazy(
  () => import('src/pages/dashboard/bookings/calendar'),
);
const BookingsDetailsPage = lazy(
  () => import('src/pages/dashboard/bookings/details'),
);

// Transactions
const TransactionsListPage = lazy(
  () => import('src/pages/dashboard/transactions/list'),
);

// Events
const EventListPage = lazy(() => import('src/pages/dashboard/event/list'));
const EventCreatePage = lazy(() => import('src/pages/dashboard/event/new'));
const EventViewPage = lazy(() => import('src/pages/dashboard/event/view'));

// Classes
const ClassCalendarPage = lazy(
  () => import('src/pages/dashboard/class/calendar'),
);
const ClassCreatePage = lazy(() => import('src/pages/dashboard/class/new'));
const ClassDetailsPage = lazy(() => import('src/pages/dashboard/class/view'));
const ClassEditPage = lazy(() => import('src/pages/dashboard/class/edit'));
const ClassSessionEditPage = lazy(
  () => import('src/pages/dashboard/class/edit-session'),
);
const ClassListPage = lazy(() => import('src/pages/dashboard/class/list'));
const ClassDuplicatePage = lazy(
  () => import('src/pages/dashboard/class/duplicate'),
);

// Customers
const CustomersListPage = lazy(
  () => import('src/pages/dashboard/customers/list'),
);
const CustomersDetailsPage = lazy(
  () => import('src/pages/dashboard/customers/view'),
);
//campaigns
const CampaignListPage = lazy(
  () => import('src/pages/dashboard/campaigns/list'),
);
const CampaignCreatePage = lazy(
  () => import('src/pages/dashboard/campaigns/new'),
);

// Gallery
// Admins
const AdminsListPage = lazy(() => import('src/pages/dashboard/admins/list'));
const AdminsCreatePage = lazy(() => import('src/pages/dashboard/admins/new'));
const AdminsEditPage = lazy(() => import('src/pages/dashboard/admins/edit'));

// Roles
const RolesListPage = lazy(() => import('src/pages/dashboard/roles/list'));
const RolesCreatePage = lazy(() => import('src/pages/dashboard/roles/new'));
const RolesEditPage = lazy(() => import('src/pages/dashboard/roles/edit'));

// Settings
const GalleryViewPage = lazy(() => import('src/pages/dashboard/gallery/view'));
const ContactViewPage = lazy(() => import('src/pages/dashboard/contacts/view'));
// Error
const Page403 = lazy(() => import('src/pages/error/403'));
const Page404 = lazy(() => import('src/pages/error/404'));

// Tournaments
const TournamentsListPage = lazy(
  () => import('src/pages/dashboard/tournaments/list'),
);
const TournamentCreatePage = lazy(
  () => import('src/pages/dashboard/tournaments/new'),
);
const TournamentEditPage = lazy(
  () => import('src/pages/dashboard/tournaments/edit'),
);
const TournamentDetailsPage = lazy(
  () => import('src/pages/dashboard/tournaments/details'),
);
const TournamentDivisionOverview = lazy(
  () => import('src/pages/dashboard/tournaments/division-overview'),
);
const TournamentDivisionNew = lazy(
  () => import('src/pages/dashboard/tournaments/division-new'),
);
const TournamentDivisionEdit = lazy(
  () => import('src/pages/dashboard/tournaments/division-edit'),
);
const TournamentDivisionTeams = lazy(
  () => import('src/pages/dashboard/tournaments/division-teams'),
);
const TournamentDivisionGroups = lazy(
  () => import('src/pages/dashboard/tournaments/division-groups'),
);
const TournamentDivisionMatches = lazy(
  () => import('src/pages/dashboard/tournaments/division-matches'),
);
// Trips
const TripListPage = lazy(() => import('src/pages/dashboard/trip/list'));
const TripCreatePage = lazy(() => import('src/pages/dashboard/trip/new'));
const TripEditPage = lazy(() => import('src/pages/dashboard/trip/edit'));
const TripViewPage = lazy(() => import('src/pages/dashboard/trip/view'));

// Customer Groups
const CustomerGroupListPage = lazy(
  () => import('src/pages/dashboard/customer-groups/list'),
);
const CustomerGroupCreatePage = lazy(
  () => import('src/pages/dashboard/customer-groups/new'),
);
const CustomerGroupDetailsPage = lazy(
  () => import('src/pages/dashboard/customer-groups/details'),
);
const CustomerGroupEditPage = lazy(
  () => import('src/pages/dashboard/customer-groups/edit'),
);

// ----------------------------------------------------------------------

export interface IRoute {
  path?: string;
  element?: React.ReactNode;
  module?: ModuleNameEnum;
  children?: IRoute[];
  index?: boolean;
}

const layoutContent = (
  <DashboardLayout>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

export const dashboardRoutes: IRoute[] = [
  {
    path: '/dashboard/auth',

    element: (
      <Suspense fallback={<SplashScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: 'sign-in',
        element: (
          <GuestGuard>
            <AuthSplitLayout>
              <Jwt.SignInPage />
            </AuthSplitLayout>
          </GuestGuard>
        ),
        index: true,
      },
      {
        path: 'forget-password',
        element: (
          <GuestGuard>
            <AuthSplitLayout>
              <Jwt.ForgetPasswordPage />
            </AuthSplitLayout>
          </GuestGuard>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <GuestGuard>
            <AuthSplitLayout>
              <Jwt.ResetPasswordPage />
            </AuthSplitLayout>
          </GuestGuard>
        ),
      },
    ],
  },
  {
    path: '/dashboard',
    element: CONFIG.auth.skip ? (
      layoutContent
    ) : (
      <AuthGuard>{layoutContent}</AuthGuard>
    ),
    children: [
      {
        path: 'overview',
        module: ModuleNameEnum.DASHBOARD,
        children: [
          {
            path: 'booking',
            element: (
              <PermissionGuard
                requiredPermission={DashboardPermission.view}
                isPage
              >
                <OverviewBookingPage />
              </PermissionGuard>
            ),
          },
          {
            path: 'finance',
            element: (
              <PermissionGuard
                requiredPermission={DashboardPermission.view}
                isPage
              >
                <OverviewFinancePage />
              </PermissionGuard>
            ),
          },
          {
            path: 'location',
            element: (
              <PermissionGuard
                requiredPermission={DashboardPermission.view}
                isPage
              >
                <OverviewLocationPage />
              </PermissionGuard>
            ),
          },
          {
            path: 'customer',
            element: (
              <PermissionGuard
                requiredPermission={DashboardPermission.view}
                isPage
              >
                <OverviewCustomerPage />
              </PermissionGuard>
            ),
          },
          {
            path: 'user-statistics',
            element: (
              <PermissionGuard
                requiredPermission={DashboardPermission.view}
                isPage
              >
                <OverviewUserStatisticsPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: '',
        children: [
          {
            element: (
              <PermissionGuard
                requiredPermission={LocationPermission.list}
                isPage
              >
                <LocationListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          { path: '403', element: <Page403 /> },
          { path: '404', element: <Page404 /> },
        ],
      },
      {
        path: 'profile',
        children: [{ element: <ProfilePage />, index: true }],
      },
      {
        path: 'locations',
        module: ModuleNameEnum.LOCATIONS,
        children: [
          {
            element: (
              <PermissionGuard
                requiredPermission={LocationPermission.list}
                isPage
              >
                <LocationListPage />{' '}
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: ':id',
            element: (
              <PermissionGuard
                requiredPermission={LocationPermission.view}
                isPage
              >
                <LocationViewPage />
              </PermissionGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <PermissionGuard
                requiredPermission={LocationPermission.add}
                isPage
              >
                <LocationCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard
                requiredPermission={LocationPermission.edit}
                isPage
              >
                <LocationEditPage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/spaces/new',
            element: (
              <PermissionGuard requiredPermission={SpacePermission.add} isPage>
                <SpaceCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/spaces/:spaceId',
            element: (
              <PermissionGuard requiredPermission={SpacePermission.edit} isPage>
                <SpaceViewPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'banners',
        module: ModuleNameEnum.BANNERS,
        children: [
          {
            element: (
              <PermissionGuard
                requiredPermission={BannerPermission.list}
                isPage
              >
                <BannersListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard requiredPermission={BannerPermission.add} isPage>
                <BannersCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard
                requiredPermission={BannerPermission.edit}
                isPage
              >
                <BannersEditPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'addons',
        module: ModuleNameEnum.ADDONS,
        children: [
          {
            element: (
              <PermissionGuard
                requiredPermission={AddonPermission.list}
                isPage
              >
                <AddonsListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard requiredPermission={AddonPermission.add} isPage>
                <AddonsCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard
                requiredPermission={AddonPermission.edit}
                isPage
              >
                <AddonsEditPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'spaces',
        module: ModuleNameEnum.SPACES,
        children: [
          { element: <SpaceListPage />, index: true },
          { path: ':id', element: <SpaceViewPage /> },
          { path: 'new', element: <SpaceCreatePage /> },
          { path: ':id/edit', element: <SpaceEditPage /> },
        ],
      },
      {
        path: 'bookings',
        module: ModuleNameEnum.BOOKINGS,
        children: [
          {
            path: 'list',
            element: (
              <PermissionGuard
                requiredPermission={BookingPermission.list}
                isPage
              >
                <BookingsListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'calendar',
            element: (
              <PermissionGuard
                requiredPermission={BookingPermission.calender}
                isPage
              >
                <BookingsCalendarPage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionGuard
                requiredPermission={BookingPermission.view}
                isPage
              >
                <BookingsDetailsPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'transactions',
        module: ModuleNameEnum.TRANSACTIONS,
        children: [
          {
            element: (
              <PermissionGuard
                requiredPermission={TransactionPermission.list}
                isPage
              >
                <TransactionsListPage />
              </PermissionGuard>
            ),
            index: true,
          },
        ],
      },
      {
        path: 'events',
        module: ModuleNameEnum.CLASSES,
        children: [
          {
            element: (
              <PermissionGuard requiredPermission={EventPermission.list} isPage>
                <EventListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard requiredPermission={EventPermission.add} isPage>
                <EventCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionGuard requiredPermission={EventPermission.view} isPage>
                <EventViewPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'admins',
        module: ModuleNameEnum.AUTHENTICATION_ACL,
        children: [
          {
            element: (
              <PermissionGuard requiredPermission={AdminPermission.list} isPage>
                <AdminsListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard requiredPermission={AdminPermission.add} isPage>
                <AdminsCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard requiredPermission={AdminPermission.edit} isPage>
                <AdminsEditPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'roles',
        module: ModuleNameEnum.AUTHENTICATION_ACL,
        children: [
          {
            element: (
              <PermissionGuard requiredPermission={RolePermission.list} isPage>
                <RolesListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard requiredPermission={RolePermission.add} isPage>
                <RolesCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard requiredPermission={RolePermission.edit} isPage>
                <RolesEditPage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'classes',
        module: ModuleNameEnum.CLASSES,
        children: [
          { path: 'calendar', element: <ClassCalendarPage /> },
          { path: 'list', element: <ClassListPage /> },
          { path: 'new', element: <ClassCreatePage /> },
          { path: ':classId/edit', element: <ClassEditPage /> },
          { path: ':classId/duplicate', element: <ClassDuplicatePage /> },
          {
            path: ':classId/sessions/:sessionId/edit',
            element: <ClassSessionEditPage />,
          },
          {
            path: ':classId/sessions/:sessionId',
            element: <ClassDetailsPage />,
          },
        ],
      },
      {
        path: 'campaigns',
        module: ModuleNameEnum.CAMPAIGNS,
        children: [
          {
            element: (
              <PermissionGuard
                requiredPermission={CampaignPermission.list}
                isPage
              >
                <CampaignListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard
                requiredPermission={CampaignPermission.add}
                isPage
              >
                <CampaignCreatePage />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: 'customers',
        module: ModuleNameEnum.CUSTOMERS,
        children: [
          { element: <CustomersListPage />, index: true },
          { path: ':id/view', element: <CustomersDetailsPage /> },
        ],
      },
      {
        path: 'settings',
        module: ModuleNameEnum.SETTINGS,
        children: [
          { path: 'gallery', element: <GalleryViewPage />, index: true },
          { path: 'contacts', element: <ContactViewPage /> },
        ],
      },
      {
        path: 'tournaments',
        module: ModuleNameEnum.TOURNAMENTS,

        children: [
          {
            path: 'list',
            element: (
              <PermissionGuard
                requiredPermission={TournamentPermission.list}
                isPage
              >
                <TournamentsListPage />
              </PermissionGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <PermissionGuard
                requiredPermission={TournamentPermission.add}
                isPage
              >
                <TournamentCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard
                requiredPermission={TournamentPermission.edit}
                isPage
              >
                <TournamentEditPage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id',
            children: [
              {
                index: true,
                element: (
                  <PermissionGuard
                    requiredPermission={TournamentPermission.view}
                    isPage
                  >
                    <TournamentDetailsPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'divisions',
                element: (
                  <PermissionGuard
                    requiredPermission={TournamentDivisionPermission.view}
                    isPage
                  >
                    <DivisionLayout>
                      <Outlet />
                    </DivisionLayout>
                  </PermissionGuard>
                ),
                children: [
                  {
                    path: 'new',
                    element: (
                      <PermissionGuard
                        requiredPermission={TournamentDivisionPermission.add}
                        isPage
                      >
                        <TournamentDivisionNew />
                      </PermissionGuard>
                    ),
                  },
                  {
                    path: ':divisionId',
                    children: [
                      {
                        path: 'edit',
                        element: (
                          <PermissionGuard
                            requiredPermission={TournamentDivisionPermission.edit}
                            isPage
                          >
                            <TournamentDivisionEdit />
                          </PermissionGuard>
                        ),
                      },
                      {
                        index: true,
                        path: 'overview',
                        element: (
                          <PermissionGuard
                            requiredPermission={TournamentDivisionPermission.view}
                            isPage
                          >
                            <TournamentDivisionOverview />
                          </PermissionGuard>
                        ),
                      },
                      {
                        path: 'teams',
                        element: (
                          <PermissionGuard
                            requiredPermission={TournamentTeamPermission.list}
                            isPage
                          >
                            <TournamentDivisionTeams />
                          </PermissionGuard>
                        ),
                      },
                      {
                        path: 'groups',
                        element: (
                          <PermissionGuard
                            requiredPermission={TournamentGroupPermission.list}
                            isPage
                          >
                            <TournamentDivisionGroups />
                          </PermissionGuard>
                        ),
                      },
                      {
                        path: 'matches',
                        element: (
                          <PermissionGuard
                            requiredPermission={TournamentMatchPermission.list}
                            isPage
                          >
                            <TournamentDivisionMatches />
                          </PermissionGuard>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'trips',
        module: ModuleNameEnum.TRIPS,
        children: [
          { element: <TripListPage />, index: true },
          { path: 'new', element: <TripCreatePage /> },
          { path: ':id/edit', element: <TripEditPage /> },
          { path: ':id', element: <TripViewPage /> },
        ],
      },
      {
        path: 'customer-groups',
        module: ModuleNameEnum.CUSTOMER_GROUPS,
        children: [
          {
            element: (
              <PermissionGuard
                requiredPermission={CustomerGroupPermission.list}
                isPage
              >
                <CustomerGroupListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard
                requiredPermission={CustomerGroupPermission.add}
                isPage
              >
                <CustomerGroupCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionGuard
                requiredPermission={CustomerGroupPermission.view}
                isPage
              >
                <CustomerGroupDetailsPage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard
                requiredPermission={CustomerGroupPermission.edit}
                isPage
              >
                <CustomerGroupEditPage />
              </PermissionGuard>
            ),
          },
        ],
      },
    ],
  },
];
